/**
 * Integration role labels — disambiguate a provider that a workflow needs for
 * MORE THAN ONE purpose.
 *
 * Some providers show up in several requirement OR-groups serving DIFFERENT
 * purposes. The canonical case is Lark on the prd-review agent:
 *   - doc_source  { any: [google, notion, lark] }  → Lark as a DOCUMENT SOURCE
 *                                                     (lark-docs skill, docx API)
 *   - chat_notify { any: [slack, lark] }            → Lark as a CHAT/NOTIFY target
 *                                                     (lark messaging, im API)
 * Both are correct — Lark genuinely serves two roles — but rendering two bare
 * "Lark" cards is confusing. So we role-qualify them: "Lark Docs" + "Lark Chat".
 *
 * The role is derived from the GROUP'S PURPOSE, not from a per-provider check.
 * A requirement OR-group's member SET uniquely identifies the backend meta-skill
 * that produced it (see backend/src/services/skill-integrations.js —
 * REQUIRED_INTEGRATION_MAP): doc_source, chat_notify, git-write. We map that
 * group purpose → a human role suffix. A provider that appears in only ONE group
 * (GitHub, Notion, Slack, Google Docs, …) is unambiguous and keeps its plain
 * name — suffixes are added ONLY to providers that appear across multiple groups
 * with different roles.
 *
 * IMPORTANT: this only changes the DISPLAY NAME. Every role-qualified card keeps
 * the SAME provider `id` (e.g. both Lark cards are id `lark`), so they are backed
 * by the ONE shared Lark connection — connecting/selecting either drives the
 * single underlying integration; only the label differs.
 */

/** Canonical, order-independent key for an OR-group's member set. */
export function groupKey(members) {
  return [...new Set((members || []).filter((m) => typeof m === 'string'))]
    .sort()
    .join(',');
}

/**
 * OR-group PURPOSE → human role suffix, keyed by the group's member set. The key
 * mirrors the backend meta-skills in skill-integrations.js (the member tuple is a
 * stable fingerprint of the meta-skill that emitted the group):
 *   doc_source  {google,notion,lark} → "Docs"  (a document source)
 *   chat_notify {slack,lark}         → "Chat"  (a chat / notify target)
 *   git-write   {github,gitlab}      → "Code"  (a code host)
 * Groups whose members are variants of the SAME provider (e.g. linkedin
 * personal/business) intentionally have no entry — there's nothing to
 * disambiguate against another provider.
 */
const GROUP_ROLE_BY_MEMBERS = Object.freeze({
  'google,lark,notion': 'Docs',
  'lark,slack': 'Chat',
  'github,gitlab': 'Code',
});

/** The role suffix for one OR-group (by its members), or null if it has none. */
export function roleForGroup(members) {
  return GROUP_ROLE_BY_MEMBERS[groupKey(members)] || null;
}

/**
 * Given the requirement entry lists actually being rendered (each a mix of
 * provider-id strings and `{ any: [...] }` OR-groups), return the SET of provider
 * ids that appear across MULTIPLE groups serving DIFFERENT roles. Only these get
 * a role suffix; every other provider keeps its plain name.
 *
 * @param  {...(Array<string|{any:string[]}>)} entryLists  e.g. (required, optional)
 * @returns {Set<string>} provider ids that are ambiguous across roles
 */
export function multiRoleProviders(...entryLists) {
  const rolesById = new Map(); // id -> Set<role>
  for (const list of entryLists) {
    for (const entry of Array.isArray(list) ? list : []) {
      if (!entry || typeof entry !== 'object' || !Array.isArray(entry.any)) continue;
      const role = roleForGroup(entry.any);
      if (!role) continue;
      for (const id of entry.any) {
        if (typeof id !== 'string') continue;
        if (!rolesById.has(id)) rolesById.set(id, new Set());
        rolesById.get(id).add(role);
      }
    }
  }
  const multi = new Set();
  for (const [id, roles] of rolesById) if (roles.size >= 2) multi.add(id);
  return multi;
}

/**
 * The display name for one provider row — role-qualified ONLY when the provider
 * is multi-role AND this row belongs to a role-bearing group. Plain-string rows
 * (pass members = null) and single-role providers keep their base name.
 *
 * @param {string} baseName   the provider's plain display name (from registry)
 * @param {string} id         the provider id (unchanged — connection identity)
 * @param {?string[]} members the OR-group members this row belongs to (or null)
 * @param {?Set<string>} multiSet the multiRoleProviders() set
 * @returns {string} the (possibly role-qualified) display name
 */
export function roleQualifiedName(baseName, id, members, multiSet) {
  if (!members || !multiSet || !multiSet.has(id)) return baseName;
  const role = roleForGroup(members);
  return role ? `${baseName} ${role}` : baseName;
}
